import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithCustomToken } from "firebase/auth";
import { auth, WEBSITE_URL } from "@lib/firebase";
import LoadingScreen from "@/components/LoadingScreen";
import wordmarkLight from "@/assets/img/layout/stocko_black_logo_dashboard.svg";
import wordmarkDark from "@/assets/img/layout/stocko_white_logo_dashboard.svg";

/**
 * Landing point for the sign-in handoff from stocko.io.
 *
 * The marketing site verifies the password login, mints a custom token server
 * side and sends the browser here with the token in the URL fragment. Redeeming
 * it establishes a Firebase session on *this* origin, which is the whole point:
 * auth state does not cross origins on its own.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("working");

  useEffect(() => {
    const token = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("t");

    if (!token) {
      // No token: someone bookmarked /auth/callback or the redirect was stripped.
      setStatus("missing");
      return;
    }

    // Drop the token from the address bar before anything can await on it, so it
    // does not linger in history or get copied out of the URL bar.
    window.history.replaceState(null, "", window.location.pathname);

    signInWithCustomToken(auth, token)
      .then(() => navigate("/", { replace: true }))
      .catch(() => setStatus("failed"));
  }, [navigate]);

  if (status === "working") {
    return <LoadingScreen message="Signing you in..." />;
  }

  const message =
    status === "failed"
      ? "That sign-in link has expired."
      : "Sign in to continue to your dashboard.";

  // Same full-screen treatment as the loading state, so the page does not jump
  // between two different layouts as the token is redeemed.
  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-6 bg-[#eaeef3] px-6 text-center dark:bg-navy-900">
      <img
        src={wordmarkLight}
        alt="Stocko"
        className="h-8 w-auto select-none dark:hidden"
        draggable="false"
      />
      <img
        src={wordmarkDark}
        alt="Stocko"
        className="hidden h-8 w-auto select-none dark:block"
        draggable="false"
      />
      <p className="text-[17px] text-[#2b2b2b] dark:text-gray-300">{message}</p>
      <a
        href={`${WEBSITE_URL}/login`}
        className="linear rounded-xl bg-brand-500 px-6 py-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700"
      >
        Go to sign in
      </a>
    </div>
  );
}
